import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import css from './ChartWidgetWrapper.css';
import { WIDGET_DATA_TYPES } from '../../../customDashboards/widgetDataTypes';
import OverflowingText from '../../foundations/OverflowingText';
import Tooltip from '../../molecules/Tooltip';
import Spinner from '../../widgets/Spinner';
import { EmptyState } from '../../widgets';
import { ACTION_TYPES, WIDGET_TYPE_TO_SCHEMA } from './consts';
import Icon, { IconSizes, IconVariants } from '../../foundations/Icon';
import { WIDGET_TYPE_TOTALS } from '../../../customDashboards/widgetShelf';

function ChartWidgetWrapper({
    widgetTitle,
    widgetType,
    widgetDataType,
    showSpinner,
    showNoData,
    blockedFilters = [],
    showError,
    onMenuClick = () => {},
    onCloneClick = () => {},
    onDeleteClick = () => {},
    onExportClick = () => {},
    allowedActions = Object.values(ACTION_TYPES),
    children = null,
}) {
    return (
        <>
            <div className={css.gridWidgetHeader}>
                <div className={css.gridHeaderDataType}>
                    <span className={css.gridHeaderDataTypeIcon}>{WIDGET_DATA_TYPES[widgetDataType].icon}</span>
                    <div className={css.gridHeaderDataTypeText}>{WIDGET_DATA_TYPES[widgetDataType].display_name}</div>
                </div>
                <OverflowingText className={css.gridHeaderContents}>{widgetTitle}</OverflowingText>

                <div className={css.gridIconsSector} onMouseDown={e => e.stopPropagation()}>
                    {allowedActions.includes(ACTION_TYPES.TOGGLE_MENU) && (
                        <Tooltip
                            wrapperClass={css.gridIconContainer}
                            interactive
                            titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.EDIT_WIDGET"
                            position="bottom"
                            distance={25}
                        >
                            <Icon
                                name="settings"
                                className={css.widgetIcon}
                                variant={IconVariants.LIGHT}
                                size={IconSizes.SMALL}
                                onClick={onMenuClick}
                            />
                        </Tooltip>
                    )}
                    {allowedActions.includes(ACTION_TYPES.CLONE) && (
                        <Tooltip
                            wrapperClass={css.gridIconContainer}
                            interactive
                            titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.CLONE_WIDGET"
                            position="bottom"
                            distance={25}
                        >
                            <Icon
                                name="copy"
                                className={css.widgetIcon}
                                variant={IconVariants.LIGHT}
                                size={IconSizes.SMALL}
                                onClick={onCloneClick}
                            />
                        </Tooltip>
                    )}
                    {allowedActions.includes(ACTION_TYPES.EXPORT) && (
                        <Tooltip
                            wrapperClass={css.gridIconContainer}
                            interactive
                            titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.EXPORT_WIDGET"
                            position="bottom"
                            distance={25}
                        >
                            <Icon
                                name="download"
                                className={css.widgetIcon}
                                variant={IconVariants.LIGHT}
                                size={IconSizes.SMALL}
                                onClick={onExportClick}
                            />
                        </Tooltip>
                    )}
                    {allowedActions.includes(ACTION_TYPES.DELETE) && (
                        <Tooltip
                            wrapperClass={css.gridIconContainer}
                            interactive
                            titleTranslationKey="STATIC.PAGES.DASHBOARD.ACTIONS.DELETE_WIDGET"
                            position="bottom"
                            distance={25}
                        >
                            <Icon
                                name="trash"
                                className={css.widgetIcon}
                                variant={IconVariants.LIGHT}
                                size={IconSizes.SMALL}
                                onClick={e => {
                                    e.stopPropagation();
                                    onDeleteClick();
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
            </div>
            <div className={classNames(css.gridWidgetContent, { [css.totals]: widgetType === WIDGET_TYPE_TOTALS })}>
                {blockedFilters.length ? (
                    <div
                        className={classNames(
                            css.gridWidgetCenterContainer,
                            css.gridWidgetCenterNoData,
                            css.gridWidgetBlockedFilters
                        )}
                    >
                        <Icon disabled name="filter-off" variant={IconVariants.LIGHT} size={IconSizes.X_LARGE} />
                        <div>
                            <Translate id="STATIC.PAGES.DASHBOARD.WIDGET.QUERY_BLOCKED_FILTERS" />
                            <br />
                            <Translate
                                id="STATIC.PAGES.DASHBOARD.WIDGET.QUERY_BLOCKED_FILTERS_DETAILS"
                                data={{
                                    reportType: WIDGET_DATA_TYPES[widgetDataType].display_name,
                                    filterNames: blockedFilters.join(', '),
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        {showSpinner && (
                            <div className={css.gridWidgetCenterContainer}>
                                <Spinner show />
                            </div>
                        )}
                        {showNoData && (
                            <div className={css.gridWidgetCenterNoData}>
                                <EmptyState
                                    headerStyle={{ color: 'var(--gray500)', fontSize: '15px' }}
                                    customIcon={WIDGET_TYPE_TO_SCHEMA[widgetType]}
                                    header="STATIC.PAGES.DASHBOARD.WIDGET.QUERY_NO_DATA"
                                />
                            </div>
                        )}
                        {showError && (
                            <div className={css.gridWidgetCenterContainer}>
                                <EmptyState
                                    header={
                                        allowedActions.includes(ACTION_TYPES.TOGGLE_MENU)
                                            ? 'STATIC.PAGES.DASHBOARD.WIDGET.QUERY_ERROR'
                                            : 'STATIC.PAGES.DASHBOARD.WIDGET.QUERY_ERROR_QUICK_REPORT'
                                    }
                                />
                            </div>
                        )}
                    </>
                )}
                {children}
            </div>
        </>
    );
}

ChartWidgetWrapper.propTypes = {
    widgetTitle: PropTypes.string.isRequired,
    widgetType: PropTypes.string.isRequired,
    widgetDataType: PropTypes.string.isRequired,
    showSpinner: PropTypes.bool.isRequired,
    showNoData: PropTypes.bool.isRequired,
    blockedFilters: PropTypes.arrayOf(PropTypes.string),
    showError: PropTypes.bool.isRequired,
    onMenuClick: PropTypes.func,
    onCloneClick: PropTypes.func,
    onDeleteClick: PropTypes.func,
    onExportClick: PropTypes.func,
    allowedActions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ACTION_TYPES))),
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export default memo(ChartWidgetWrapper);
